import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, googleProvider, facebookProvider } from '../config/firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { UserService } from '../services/userService';
import { redirectAfterAuth } from '../utils/authReturnTo';
import googleAuthService from '../services/googleAuthService';
import authApiService from '../services/authApiService';
import venueApiService from '../services/venueApi';
import {
  fetchBackendUserProfile,
  mergeStoredUserWithBackendProfile,
  resolveUserDisplayName,
} from '../utils/userDisplayName';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Sign up with email and password
  const signup = async (email, password, displayName, additionalData = {}) => {
    try {
      setError('');
      const result = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }
      
      // Create user profile in PostgreSQL
      try {
        const profile = await UserService.createUserProfile(result.user, {
          displayName,
          ...additionalData
        });
        setUserProfile(profile);
      } catch (profileError) {
        // Don't fail the signup if profile creation fails
      }
      
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Sign in with email and password
  const login = async (email, password, userType = 'customer') => {
    try {
      setError('');
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Load user profile based on user type
      try {
        const profile = await UserService.getUserProfile(result.user);
        
        // Check if user is admin based on role from API
        const isAdmin = profile.role === 'admin';
        const userTypeFromApi = profile.role || profile.user_type || 'customer';
        
        // Set user profile with role-based admin status
        const userProfile = {
          ...profile,
          user_type: userTypeFromApi,
          is_admin: isAdmin
        };
        
        setUserProfile(userProfile);
        
        // Check if user type matches (for venue owners)
        if (userType === 'venue_owner' && userTypeFromApi !== 'venue_owner' && !isAdmin) {
          throw new Error('Invalid venue owner credentials');
        }
      } catch (profileError) {
        if (userType === 'venue_owner') {
          throw new Error('Invalid venue owner credentials');
        }
      }
      
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Sign in with Google (Legacy Firebase method)
  const loginWithGoogle = async () => {
    try {
      setError('');
      const result = await signInWithPopup(auth, googleProvider);
      
      // Check if user profile exists in PostgreSQL, create if not
      try {
        const profile = await UserService.getUserProfile(result.user);
        
        // Check if user is admin based on role from API
        const isAdmin = profile.role === 'admin';
        const userTypeFromApi = profile.role || profile.user_type || 'customer';
        
        // Set user profile with role-based admin status
        const userProfile = {
          ...profile,
          user_type: userTypeFromApi,
          is_admin: isAdmin
        };
        
        setUserProfile(userProfile);
      } catch (profileError) {
        // User doesn't exist in PostgreSQL, create profile
        try {
          const profile = await UserService.createUserProfile(result.user);
          setUserProfile(profile);
        } catch (createError) {
        }
      }
      
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // New Google OAuth with Backend Integration
  const loginWithGoogleOAuth = async () => {
    try {
      setError('');
      // Don't set loading to true here - this causes the white background issue
      

      // Initialize Google OAuth if not already done
      await googleAuthService.initializeGoogleAuth();

      // Trigger Google OAuth login
      const result = await googleAuthService.login();

      if (result.success) {
        // Check if user is admin based on role from API
        const isAdmin = result.user.role === 'admin';
        const userTypeFromApi = result.user.role || 'customer';
        
        // Use venue data from backend if present
        let venueId = result.user.venue_id || null;
        let ownedVenues = result.user.owned_venues || [];
        let finalUserType = userTypeFromApi;
        
        // If venue_owner but no venue data, or customer who owns venues, fetch by email
        const needsVenueFetch = (userTypeFromApi === 'venue_owner' || !venueId) && !ownedVenues?.length && result.user.email;
        if (needsVenueFetch) {
          try {
            const fetchedVenues = await venueApiService.getVenuesByOwner(result.user.email);
            if (fetchedVenues?.length > 0) {
              ownedVenues = fetchedVenues;
              venueId = fetchedVenues[0].id;
              if (userTypeFromApi !== 'venue_owner') {
                finalUserType = 'venue_owner'; // Upgrade: user owns venues
              }
            }
          } catch {
            // Continue without venue data
          }
        }
        
        // Set the user profile from backend response
        const userProfileData = {
          firebase_uid: result.user.id, // Use backend user ID as primary ID
          backend_user_id: result.user.id,
          email: result.user.email,
          display_name: `${result.user.first_name} ${result.user.last_name}`.trim(),
          first_name: result.user.first_name,
          last_name: result.user.last_name,
          user_type: finalUserType,
          role: result.user.role,
          phone: result.user.phone || '',
          company: result.user.company || '',
          bio: result.user.bio || '',
          profile_image_url: result.user.profile_image_url || '',
          is_admin: isAdmin,
          venue_id: venueId,
          owned_venues: ownedVenues
        };
        
        setUserProfile(userProfileData);
        
        // Set current user for compatibility
        const currentUserData = {
          uid: result.user.id,
          email: result.user.email,
          displayName: `${result.user.first_name} ${result.user.last_name}`.trim(),
          photoURL: result.user.profile_image_url
        };
        
        setCurrentUser(currentUserData);
        

        if (redirectAfterAuth()) {
          return result;
        }

        return result;
      } else {
        throw new Error('Google OAuth failed');
      }
    } catch (error) {
      // Set a user-friendly error message
      let errorMessage = error.message || 'Failed to sign in with Google. Please try again.';
      
      // Provide more helpful error messages for common scenarios
      if (errorMessage.includes('cancelled')) {
        errorMessage = 'Login was cancelled. Please try again.';
      } else if (errorMessage.includes('timed out')) {
        errorMessage = 'Login timed out. Please try again.';
      } else if (errorMessage.includes('Failed to create user')) {
        errorMessage = 'Unable to create your account. Please contact support if this issue persists.';
      } else if (errorMessage.includes('500')) {
        errorMessage = 'Server error. Please try again in a moment.';
      } else if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError') || errorMessage.includes('Network request failed')) {
        errorMessage = 'Unable to reach the server. Please check your connection and try again.';
      }
      
      setError(errorMessage);
      throw error;
    }
    // Removed finally block that was setting loading to false
  };

  // Sign in with Facebook
  const loginWithFacebook = async () => {
    try {
      setError('');
      const result = await signInWithPopup(auth, facebookProvider);
      
      // Check if user profile exists in PostgreSQL, create if not
      try {
        const profile = await UserService.getUserProfile(result.user);
        setUserProfile(profile);
      } catch (profileError) {
        // User doesn't exist in PostgreSQL, create profile
        try {
          const profile = await UserService.createUserProfile(result.user);
          setUserProfile(profile);
        } catch (createError) {
        }
      }
      
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Register with email/password (backend API — no Firebase)
  const registerWithEmail = async ({ email, password, first_name, last_name, role = 'customer', phone = '' }) => {
    try {
      setError('');
      const result = await authApiService.register({ email, password, first_name, last_name, role, phone });
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Login with email/password (backend API — no Firebase)
  const loginWithEmail = async (email, password) => {
    try {
      setError('');
      const result = await authApiService.login(email, password);

      if (result.success && result.access_token) {
        localStorage.setItem('access_token', result.access_token);
        localStorage.setItem('user', JSON.stringify(result.user));

        const user = result.user;
        const isAdmin = user.role === 'admin';

        let venueId = user.venue_id || null;
        let ownedVenues = user.owned_venues || [];
        let finalUserType = user.role || 'customer';

        if ((finalUserType === 'venue_owner' || !venueId) && !ownedVenues?.length && user.email) {
          try {
            const fetched = await venueApiService.getVenuesByOwner(user.email);
            if (fetched?.length > 0) {
              ownedVenues = fetched;
              venueId = fetched[0].id;
              if (finalUserType !== 'venue_owner') finalUserType = 'venue_owner';
            }
          } catch {}
        }

        const userProfileData = {
          firebase_uid: user.id,
          backend_user_id: user.id,
          email: user.email,
          display_name: `${user.first_name} ${user.last_name}`.trim(),
          first_name: user.first_name,
          last_name: user.last_name,
          user_type: finalUserType,
          role: user.role,
          phone: user.phone || '',
          is_admin: isAdmin,
          venue_id: venueId,
          owned_venues: ownedVenues,
        };

        setUserProfile(userProfileData);
        setCurrentUser({
          uid: user.id,
          email: user.email,
          displayName: `${user.first_name} ${user.last_name}`.trim(),
        });

        if (redirectAfterAuth()) {
          return result;
        }
      }

      return result;
    } catch (error) {
      let msg = error.message || 'Login failed';
      if (error.status === 403 && msg.toLowerCase().includes('verify')) {
        msg = 'Please verify your email before logging in. Check your inbox for the verification link.';
      } else if (error.status === 400 && msg.toLowerCase().includes('google')) {
        msg = 'This account uses Google sign-in. Please use the "Sign in with Google" button.';
      }
      setError(msg);
      throw error;
    }
  };

  // Verify email token (backend API)
  const verifyEmailToken = async (token) => {
    try {
      setError('');
      const result = await authApiService.verifyEmail(token);

      if (result.success && result.access_token) {
        localStorage.setItem('access_token', result.access_token);
        localStorage.setItem('user', JSON.stringify(result.user));

        const user = result.user;
        const userProfileData = {
          firebase_uid: user.id,
          backend_user_id: user.id,
          email: user.email,
          display_name: `${user.first_name} ${user.last_name}`.trim(),
          first_name: user.first_name,
          last_name: user.last_name,
          user_type: user.role || 'customer',
          role: user.role,
          is_admin: user.role === 'admin',
          venue_id: null,
          owned_venues: [],
        };

        setUserProfile(userProfileData);
        setCurrentUser({
          uid: user.id,
          email: user.email,
          displayName: `${user.first_name} ${user.last_name}`.trim(),
        });

        if (redirectAfterAuth()) {
          return result;
        }
      }

      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Sign out
  const logout = async () => {
    try {
      setIsLoggingOut(true);
      setError('');
      

      // Clear JWT token and user data from localStorage first
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');

      // Clear state immediately
      setCurrentUser(null);
      setUserProfile(null);

      // Try to clear Firebase authentication with timeout (but don't fail if it errors)
      const firebaseSignOutPromise = signOut(auth).catch(error => {
        return null; // Return null on error so Promise.all doesn't fail
      });
      
      const firebaseTimeout = new Promise((resolve) => {
        setTimeout(() => {
          resolve(null);
        }, 2000); // 2 second timeout
      });
      
      try {
        await Promise.race([firebaseSignOutPromise, firebaseTimeout]);
      } catch (firebaseError) {
      }
      
      // Also try to revoke Google token if available
      try {
        if (window.google && window.google.accounts && window.google.accounts.oauth2) {
          window.google.accounts.oauth2.revoke();
        }
      } catch (googleError) {
      }

      // Force redirect - use replace to avoid back button issues
      window.location.replace('/');
      
    } catch (error) {
      setError(error.message);
      // Even if there's an error, clear state and redirect
      setCurrentUser(null);
      setUserProfile(null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      // Force redirect even on error
      window.location.replace('/');
    } finally {
      // Don't set isLoggingOut to false here since we're redirecting
      // setIsLoggingOut(false);
    }
  };

  // Clear error
  const clearError = () => {
    setError('');
  };

  useEffect(() => {
    const onSessionCleared = () => {
      setCurrentUser(null);
      setUserProfile(null);
    };
    window.addEventListener('occasiaSessionCleared', onSessionCleared);
    return () => window.removeEventListener('occasiaSessionCleared', onSessionCleared);
  }, []);

  useEffect(() => {
    const checkAuthState = async () => {

      // First, check for JWT token in localStorage (from Google OAuth)
      const jwtToken = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');
      

      if (jwtToken && storedUser) {
        try {
          let userData = JSON.parse(storedUser);

          try {
            const profileResult = await fetchBackendUserProfile(jwtToken);
            if (profileResult?.ok && profileResult.profile) {
              userData = mergeStoredUserWithBackendProfile(userData, profileResult.profile);
              localStorage.setItem('user', JSON.stringify(userData));
            } else if (profileResult?.status === 401) {
              localStorage.removeItem('access_token');
              localStorage.removeItem('user');
              setLoading(false);
              return;
            }
          } catch (refreshError) {
            // Fall back to cached localStorage profile when offline, etc.
          }

          const displayName = resolveUserDisplayName(
            {
              first_name: userData.first_name,
              last_name: userData.last_name,
              display_name: userData.display_name,
            },
            { displayName: `${userData.first_name || ''} ${userData.last_name || ''}`.trim() }
          );
          
          // Set current user from stored data
          const currentUserData = {
            uid: userData.id,
            email: userData.email,
            displayName,
            photoURL: userData.profile_image_url
          };
          
          setCurrentUser(currentUserData);
          
          // Set user profile from stored data
          let userProfileData = {
            firebase_uid: userData.id,
            backend_user_id: userData.id,
            email: userData.email,
            display_name: displayName,
            first_name: userData.first_name,
            last_name: userData.last_name,
            user_type: userData.role || 'customer',
            phone: userData.phone || '',
            company: userData.company || '',
            bio: userData.bio || '',
            profile_image_url: userData.profile_image_url || '',
            is_admin: userData.role === 'admin' || userData.is_admin || false,
            venue_id: userData.venue_id || null,
            owned_venues: userData.owned_venues || []
          };
          

          
          // If user is venue_owner but doesn't have venue_id, try to fetch venues by email
          const userTypeFromApi = userProfileData.user_type;
          if (userTypeFromApi === 'venue_owner' && !userProfileData.venue_id && !userProfileData.owned_venues?.length) {
            try {
              const ownedVenues = await venueApiService.getVenuesByOwner(userProfileData.email);

              if (ownedVenues.length > 0) {
                // Update profile with venue information
                userProfileData = {
                  ...userProfileData,
                  venue_id: ownedVenues[0].id,
                  owned_venues: ownedVenues
                };

                // Update localStorage with new venue data
                const updatedUserData = {
                  ...userData,
                  venue_id: ownedVenues[0].id,
                  owned_venues: ownedVenues
                };
                localStorage.setItem('user', JSON.stringify(updatedUserData));
              } else {
              }
            } catch (venueCheckError) {
              // Continue with profile even if venue fetch fails
            }
          }
          
          setUserProfile(userProfileData);
          
          setLoading(false);
          return;
        } catch (error) {
          // Clear invalid data
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
        }
      }
      
      // If no JWT token, check Firebase authentication
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        setCurrentUser(user);
        
        if (user) {
          // Load user profile from PostgreSQL
          try {
            const profile = await UserService.getUserProfile(user);

            // Check if user is admin based on role from API
            const isAdmin = profile.role === 'admin';
            const userTypeFromApi = profile.role || profile.user_type || 'customer';

            // Set user profile with role-based admin status
            let userProfile = {
              ...profile,
              user_type: userTypeFromApi,
              is_admin: isAdmin
            };

            
            // If user is venue_owner but doesn't have venue_id, try to fetch venues by email
            const shouldFetchVenues = userTypeFromApi === 'venue_owner' && !userProfile.venue_id && !userProfile.owned_venues?.length;

            
            if (shouldFetchVenues) {
              try {
                const ownedVenues = await venueApiService.getVenuesByOwner(user.email);

                if (ownedVenues.length > 0) {
                  // Update profile with venue information
                  userProfile = {
                    ...userProfile,
                    venue_id: ownedVenues[0].id,
                    owned_venues: ownedVenues
                  };
                } else {
                }
              } catch (venueCheckError) {

                // Continue with profile even if venue fetch fails
              }
            } else {
            }

            setUserProfile(userProfile);
          } catch (error) {

            // Fallback: Check if user owns any venues by querying the venues API
            try {
              const ownedVenues = await venueApiService.getVenuesByOwner(user.email);

              let tempProfile;
              
              if (ownedVenues.length > 0) {
                // User owns venue(s) - mark as venue_owner
                tempProfile = {
                  firebase_uid: user.uid,
                  email: user.email,
                  display_name: user.displayName,
                  user_type: 'venue_owner',
                  venue_id: ownedVenues[0].id, // Use first venue as primary
                  owned_venues: ownedVenues,
                  is_admin: false
                };
              } else {
                // User doesn't own any venues - regular customer
                tempProfile = {
                  firebase_uid: user.uid,
                  email: user.email,
                  display_name: user.displayName,
                  user_type: 'customer',
                  venue_id: null,
                  owned_venues: [],
                  is_admin: false
                };
              }
              
              setUserProfile(tempProfile);
            } catch (venueCheckError) {
              // Final fallback - create basic customer profile
              const basicProfile = {
                firebase_uid: user.uid,
                email: user.email,
                display_name: user.displayName,
                user_type: 'customer',
                venue_id: null,
                owned_venues: [],
                is_admin: false
              };
              setUserProfile(basicProfile);
            }
          }
        } else {
          setUserProfile(null);
          // Only redirect to landing page on actual logout, not on initial load
          // We'll handle this in the logout function instead
        }
        
        setLoading(false);
      });

      return unsubscribe;
    };

    checkAuthState();
  }, []);

  const value = {
    currentUser,
    userProfile,
    login,
    signup,
    loginWithGoogle,
    loginWithGoogleOAuth,
    loginWithFacebook,
    registerWithEmail,
    loginWithEmail,
    verifyEmailToken,
    logout,
    error,
    clearError,
    loading,
    isLoggingOut,
    updateUserProfile: UserService.updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
